// uploadProgressBus — module-level pub/sub for active file uploads.
//
// WHY THIS EXISTS (2026-10-01): many upload flows in the app showed only a
// spinner (or nothing) while large files (e.g. 50-100 MB chat videos) were
// uploading — users reasonably assumed the app had frozen. uploadWithValidation()
// already had byte-accurate XHR progress, but each caller wired its own UI.
//
// Instead of threading a progress callback through every hook/component, ANY
// uploadWithValidation() call now also publishes progress here, and ONE
// app-root component (UploadProgressOverlay) renders a compact chip. Every
// upload site in the app gets progress for free; per-view indicators keep
// working alongside (mild duplication is acceptable; the chip is small).
//
// Teardown discipline: subscribers return an unsubscribe function; the
// overlay MUST call it in its useEffect cleanup (AGENTS.md §2).

export interface UploadProgressEvent {
    /** Stable key for one upload session (caller-provided or auto-generated). */
    id: string;
    /** Display name (file name or module label). */
    label: string;
    /** 0-100. -1 = indeterminate (e.g. awaiting the signed URL). */
    percent: number;
    /** 'uploading' | 'done' | 'error' — done/error rows auto-expire. */
    status: 'uploading' | 'done' | 'error';
}

type Listener = (event: UploadProgressEvent) => void;

const listeners = new Set<Listener>();

/** Auto-expiry for terminal rows (done/error) so the chip does not linger. */
const TERMINAL_TTL_MS = 2500;

/** Live upload rows keyed by id. Exported read-only for tests. */
export const activeUploads = new Map<string, UploadProgressEvent>();

/** Timer handles for terminal-row expiry (module-level, not per-listener). */
const expiryTimers = new Map<string, ReturnType<typeof setTimeout>>();

function notify(event: UploadProgressEvent) {
    listeners.forEach(fn => {
        try {
            fn(event);
        } catch {
            // A broken subscriber must never break the upload itself.
        }
    });
}

/**
 * Publish an upload progress update. Called from uploadWithValidation; safe
 * to call directly from any module that uploads outside that helper.
 */
export function reportUploadProgress(event: UploadProgressEvent) {
    const prev = activeUploads.get(event.id);
    const becameTerminal = event.status !== 'uploading' && prev?.status === 'uploading';

    // Keep the highest observed percent for terminal rows (upload XHR can
    // fire 100 → done in either order; never regress the shown number).
    const percent = becameTerminal && prev ? Math.max(prev.percent, event.percent) : event.percent;
    activeUploads.set(event.id, { ...event, percent });

    if (becameTerminal || (event.status !== 'uploading' && !expiryTimers.has(event.id))) {
        clearTimeout(expiryTimers.get(event.id));
        expiryTimers.set(event.id, setTimeout(() => {
            expiryTimers.delete(event.id);
            activeUploads.delete(event.id);
            // Notify AFTER deletion — the overlay re-reads the snapshot; without
            // this the terminal chip stays in the DOM until the next event
            // (owner-reported 2026-10-01: "success chip not going away").
            notify({ ...event, status: event.status });
        }, TERMINAL_TTL_MS));
    }
    notify(event);
}

/**
 * Subscribe to progress events. Returns an unsubscribe function — call it in
 * the component's useEffect teardown.
 */
export function subscribeUploadProgress(fn: Listener): () => void {
    listeners.add(fn);
    return () => {
        listeners.delete(fn);
    };
}

/** Snapshot of current rows (uploads in progress + recently finished). */
export function getActiveUploads(): UploadProgressEvent[] {
    return [...activeUploads.values()];
}
